import { useState, useEffect, useRef } from 'react';
import { Building2, Phone, Mail, Globe, Save, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { profileService, companyService } from '@/services';
import RecordMetadata from '@/components/RecordMetadata';
import type { AuditedRecord } from '@prolarus/shared';

export default function CompanyProfile() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const snapshotRef = useRef<Record<string, unknown> | null>(null);
  const [metadata, setMetadata] = useState<AuditedRecord>({
    created: null,
    modified: null,
    created_by_profile: null,
    updated_by_profile: null,
  });
  const [formData, setFormData] = useState({
    companyName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    taxId: '',
  });

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!user) return;

      try {
        // First get the user's profile to find their company_id
        const { data: profile, error: profileError } = await profileService.get(user.id);

        if (profileError) throw profileError;

        if (!profile?.company_id) {
          setIsFetching(false);
          return;
        }

        setCompanyId(profile.company_id);

        // Fetch the company data with audit info
        const { data: companyWithAudit, error: companyError } = await companyService.getWithAuditInfo(profile.company_id);

        if (companyError) throw companyError;

        if (companyWithAudit) {
          const company = companyWithAudit;
          
          // Store snapshot for history tracking
          snapshotRef.current = {
            name: company.name,
            address: company.address,
            city: company.city,
            state: company.state,
            zip_code: company.zip_code,
            country: company.country,
            phone: company.phone,
            email: company.email,
            website: company.website,
            tax_id: company.tax_id,
          };

          setFormData({
            companyName: company.name || '',
            address: company.address || '',
            city: company.city || '',
            state: company.state || '',
            zipCode: company.zip_code || '',
            country: company.country || '',
            phone: company.phone || '',
            email: company.email || '',
            website: company.website || '',
            taxId: company.tax_id || '',
          });

          setMetadata({
            created: company.created ?? null,
            modified: company.modified ?? null,
            created_by_profile: company.created_by_profile ?? null,
            updated_by_profile: company.updated_by_profile ?? null,
          });
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load company data.',
          variant: 'destructive',
        });
      } finally {
        setIsFetching(false);
      }
    };

    fetchCompanyData();
  }, [user, toast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) {
      toast({
        title: 'Error',
        description: 'No company associated with your profile.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const updateData = {
        name: formData.companyName,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        country: formData.country,
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
        tax_id: formData.taxId,
      };

      const { error } = await companyService.update(
        companyId,
        updateData,
        snapshotRef.current || {},
        { companyId, userId: user?.id }
      );

      if (error) throw error;

      // Update snapshot after successful save
      snapshotRef.current = { ...updateData };

      toast({
        title: 'Profile Updated',
        description: 'Your company profile has been saved successfully.',
      });
    } catch (error: any) {
      console.error('Error updating company:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update company profile.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="animate-slide-up max-w-4xl">
        <div className="page-header">
          <h1 className="page-title">Company Profile</h1>
          <p className="page-subtitle">Manage your organization's information and settings.</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
              <Building2 className="w-6 h-6 text-muted-foreground" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">No Company Associated</h2>
              <p className="text-sm text-muted-foreground">Your profile is not linked to a company yet.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-slide-up max-w-4xl">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">Company Profile</h1>
        <p className="page-subtitle">Manage your organization's information and settings.</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Company Info Card */}
        <div className="stat-card mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">Company Information</h2>
              <p className="text-sm text-muted-foreground">Basic details about your organization</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Company Name
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                className="input-field"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Street Address
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                City
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                State / Province
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                ZIP / Postal Code
              </label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleInputChange}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Country
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="input-field"
              />
            </div>
          </div>
        </div>

        {/* Contact Info Card */}
        <div className="stat-card mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">Contact Information</h2>
              <p className="text-sm text-muted-foreground">How customers and vendors can reach you</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="input-field pl-12"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input-field pl-12"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Website
              </label>
              <div className="relative">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="input-field pl-12"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Tax ID / EIN
              </label>
              <input
                type="text"
                name="taxId"
                value={formData.taxId}
                onChange={handleInputChange}
                className="input-field"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button type="submit" disabled={isLoading} className="btn-primary">
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Changes
              </>
            )}
          </button>
        </div>

        {/* Record Metadata */}
        <RecordMetadata record={metadata} />
      </form>
    </div>
  );
}
